import { c as _c } from "react-compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/core/utils";
const trackVariants = cva("bg-border w-full rounded-full", {
  variants: {
    size: {
      sm: "h-1",
      md: "h-2"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
const fillVariants = cva("h-full rounded-full transition-all", {
  variants: {
    tone: {
      accent: "bg-accent",
      muted: "bg-foreground/30",
      success: "bg-success",
      primary: "bg-primary"
    }
  },
  defaultVariants: {
    tone: "accent"
  }
});
const segmentVariants = cva("flex-1 rounded-full", {
  variants: {
    size: {
      sm: "h-1",
      md: "h-2"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export type ProgressProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof trackVariants> & VariantProps<typeof fillVariants> & {
  value?: number;
  max?: number;
  segments?: number;
};
export function Progress(t0) {
  const $ = _c(35);
  let className;
  let max;
  let props;
  let segments;
  let size;
  let t1;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      size,
      tone,
      value: t1,
      max,
      segments,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = max;
    $[3] = props;
    $[4] = segments;
    $[5] = size;
    $[6] = t1;
    $[7] = tone;
  } else {
    className = $[1];
    max = $[2];
    props = $[3];
    segments = $[4];
    size = $[5];
    t1 = $[6];
    tone = $[7];
  }
  const value = t1 === undefined ? 0 : t1;
  const safeSegments = segments && segments > 0 ? segments : undefined;
  const safeMax = max ?? safeSegments ?? 100;
  const clampedValue = Math.min(Math.max(value, 0), safeMax);
  const percent = safeMax > 0 ? clampedValue / safeMax * 100 : 0;
  if (safeSegments) {
    const filledSegments = Math.round(percent / 100 * safeSegments);
    let t2;
    if ($[8] !== className) {
      t2 = cn("flex w-full items-center gap-2", className);
      $[8] = className;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== safeSegments) {
      t3 = Array.from({
        length: safeSegments
      });
      $[10] = safeSegments;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== filledSegments || $[13] !== size || $[14] !== t3 || $[15] !== tone) {
      t4 = t3.map((_, index) => <span className={cn(segmentVariants({
        size
      }), index < filledSegments ? fillVariants({
        tone
      }) : "bg-border")} key={`segment-${index}`} />);
      $[12] = filledSegments;
      $[13] = size;
      $[14] = t3;
      $[15] = tone;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== props || $[18] !== t2 || $[19] !== t4) {
      t5 = <div className={t2} {...props}>{t4}</div>;
      $[17] = props;
      $[18] = t2;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  let t2;
  if ($[21] !== className || $[22] !== size) {
    t2 = cn(trackVariants({
      size,
      className
    }));
    $[21] = className;
    $[22] = size;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== tone) {
    t3 = cn(fillVariants({
      tone
    }));
    $[24] = tone;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const t4 = `${percent}%`;
  let t5;
  if ($[26] !== t4) {
    t5 = {
      width: t4
    };
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t3 || $[29] !== t5) {
    t6 = <div className={t3} style={t5} />;
    $[28] = t3;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== props || $[32] !== t2 || $[33] !== t6) {
    t7 = <div className={t2} {...props}>{t6}</div>;
    $[31] = props;
    $[32] = t2;
    $[33] = t6;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}